/* eslint-disable no-restricted-syntax */
/* eslint-disable camelcase */
import Joi from "joi";
import {
  CodeType,
  IBusinessTrxFormValues,
  IGroupedCompanies,
  IGroupedPools,
  IGroupedSegments,
  StepConfigType,
} from "./types";
import {
  IPoolsByArea, ICompanyByPool, ICreateMasterBusinessTrxRequest,
  IEditMasterBusinessTrxRequest, IGetTrxDepartmentByCompanyResponse,
} from "../../../services/businessTrxService/types";
import { IOptionItemAutoComplete } from "../../../typings/common";
import {
  Circle1, Circle2, Circle3, Circle4, Circle5, Circle6,
} from "../../../assets/icons";

export const businessTrxSchema = Joi.object({
  detailTransaksi: Joi.object({
    deskripsi: Joi.string().required().messages({
      "string.empty": "Isi deskripsi terlebih dahulu",
    }),
    deskripsiSap: Joi.string().required().messages({
      "string.empty": "Isi deskripsi SAP terlebih dahulu",
    }),
    gl: Joi.string().required().messages({
      "string.empty": "Isi GL & Deskripsi terlebih dahulu",
    }),
    tipeTransaksi: Joi.string().required().messages({
      "string.empty": "Pilih tipe transaksi terlebih dahulu",
    }),
    sap_shkzg: Joi.string().valid("H", "S", "X").required().messages({
      "string.empty": "Pilih debit/kredit terlebih dahulu",
      "any.required": "Pilih debit/kredit terlebih dahulu",
      "any.only": "Pilih debit/kredit terlebih dahulu",
    }),
    trx_code: Joi.string().required().messages({
      "string.empty": "Isi kode transaksi terlebih dahulu",
    }),
    code_type: Joi.string().allow(""),
  }),
  selectedAreaIds: Joi.array().items(Joi.string()).custom((value, helpers) => {
    const { currentStep } = helpers.state.ancestors[0];
    if (currentStep >= 1 && value.length === 0) return helpers.message({ custom: "Pilih area terlebih dahulu" });
    return value;
  }),
  selectedPoolIds: Joi.array().items(Joi.string()).custom((value, helpers) => {
    const { currentStep, poolsForValidation } = helpers.state.ancestors[0];
    if (currentStep < 2) return value;
    if (value.length === 0) return helpers.message({ custom: "Pilih pool terlebih dahulu" });
    if (poolsForValidation) {
      const pools = JSON.parse(poolsForValidation);
      const areaGroups = Object.keys(pools);
      if (areaGroups.length > 0) {
        for (const area of areaGroups) {
          const poolsInArea = pools[area].map((p: { pool_id: string }) => p.pool_id);
          const isAnyPoolSelected = poolsInArea.some((poolId: string) => value.includes(poolId));
          if (!isAnyPoolSelected) return helpers.message({ custom: "Pilih minimal 1 pool untuk setiap area terlebih dahulu" });
        }
      }
    }
    return value;
  }),
  selectedCompanyIds: Joi.array().items(Joi.string()).custom((value, helpers) => {
    const { currentStep, selectedPoolIds } = helpers.state.ancestors[0];
    if (currentStep < 3) return value;
    if (value.length === 0) return helpers.message({ custom: "Pilih perusahaan terlebih dahulu" });
    if (selectedPoolIds && selectedPoolIds.length > 0) {
      for (const poolId of selectedPoolIds) {
        const isAnyCompanySelected = value.some((companyId: string) => companyId.startsWith(`${poolId}.`));
        if (!isAnyCompanySelected) return helpers.message({ custom: "Pilih minimal 1 perusahaan untuk setiap pool terlebih dahulu" });
      }
    }
    return value;
  }),
  selectedSegmentIds: Joi.array().items(Joi.string()).custom((value, helpers) => {
    const { currentStep, selectedCompanyIds } = helpers.state.ancestors[0];
    if (currentStep < 4) return value;
    if (value.length === 0) return helpers.message({ custom: "Pilih segmen terlebih dahulu" });
    if (selectedCompanyIds && selectedCompanyIds.length > 0) {
      for (const companyId of selectedCompanyIds) {
        const isAnySegmentSelected = value.some((segmentId: string) => segmentId.startsWith(`${companyId}.`));
        if (!isAnySegmentSelected) return helpers.message({ custom: "Pilih minimal 1 segmen untuk setiap perusahaan terlebih dahulu" });
      }
    }
    return value;
  }),
  selectedDepartmentIds: Joi.array().items(Joi.string()).custom((value, helpers) => {
    const {
      currentStep, selectedSegmentIds, departmentsForValidation,
    } = helpers.state.ancestors[0];
    if (currentStep < 5) return value;
    if (value.length === 0) return helpers.message({ custom: "Pilih departemen terlebih dahulu" });

    if (selectedSegmentIds && selectedSegmentIds.length > 0 && departmentsForValidation) {
      const departmentsData: IGetTrxDepartmentByCompanyResponse = JSON
        .parse(departmentsForValidation);
      const companyDepartmentsMap = new Map<string, { department: string }[]>();
      if (departmentsData && departmentsData.list_company) {
        for (const company of departmentsData.list_company) {
          companyDepartmentsMap.set(company.company_code, company.list_department);
        }
      }

      for (const segmentId of selectedSegmentIds) {
        const [, companyCode] = segmentId.split(".");
        const departments = companyDepartmentsMap.get(companyCode);
        if (departments && departments.length > 0) {
          const isAnyDepartmentSelected = value
            .some((deptId: string) => deptId.startsWith(segmentId));
          if (!isAnyDepartmentSelected) return helpers.message({ custom: "Pilih minimal 1 departemen untuk setiap segmen terlebih dahulu" });
        }
      }
    }
    return value;
  }),
  poolsForValidation: Joi.string().optional().allow(""),
  departmentsForValidation: Joi.string().optional().allow(""),
  isEditMode: Joi.boolean(),
}).unknown(true);

// Define default form values
export const businessTrxDefaultFormValue: IBusinessTrxFormValues = {
  currentStep: 0,
  detailTransaksi: {
    deskripsi: "",
    deskripsiSap: "",
    gl: "",
    tipeTransaksi: "",
    sap_shkzg: "",
    trx_code: "",
    code_type: CodeType.Pool,
  },
  selectedAreaIds: [],
  selectedPoolIds: [],
  selectedCompanyIds: [],
  selectedCompanyRelations: [],
  selectedSegmentIds: [],
  selectedDepartmentIds: [],
  pilihSemuaPool: false,
  pilihSemuaPerusahaan: false,
  pilihSemuaSegmen: false,
  pilihSemuaDepartemen: false,
  poolsForValidation: "",
  departmentsForValidation: "",
};

/**
 * Groups a flat list of pools by area.
 * @param poolByArea - The flat array of pool data from the API.
 * @returns An object with pools grouped by area.
 */
export const groupedPools = (poolByArea: IPoolsByArea[]): IGroupedPools => poolByArea
  .reduce((acc: IGroupedPools, pool) => {
    const { area_name, pool_id, pool_name } = pool;
    if (!acc[area_name]) {
      acc[area_name] = [];
    }
    acc[area_name].push({ pool_id, pool_name });
    return acc;
  }, {});

/**
 * Groups a flat list of companies by area and then by pool.
 * @param companyByPool - The flat array of company data from the API.
 * @returns An object with companies grouped by area and pool.
 */
export const groupedCompanies = (
  companyByPool: ICompanyByPool[],
): IGroupedCompanies => companyByPool.reduce((acc: IGroupedCompanies, item) => {
  const {
    area_name, pool_name, pool_id, list_company,
  } = item;
  if (!acc[area_name]) {
    acc[area_name] = {};
  }
  acc[area_name][pool_name] = {
    pool_id,
    companies: list_company.map((c) => ({
      company_code: c.company_code,
      company_relation: c.company_relation,
    })),
  };
  return acc;
}, {});

export const formatStringCompanies = (companies: string[]) => [...new Set(companies.map((item) => item.split(".")[1]))].join(",");
export const formatStringIds = (ids: string[]) => [...new Set(ids)].join(",");
export const formatStringCompanyRelations = (relations: string[]) => {
  const relationsArray = relations.map((item) => {
    const parts = item.split(".");
    return parts.slice(1).join(".");
  });
  return [...new Set(relationsArray)].filter(Boolean).join(",");
};
export const formatStringSegments = (segments: string[]) => {
  const suffixArray: string[] = segments.map((item) => item.split(".").pop() || "");
  const uniqueSetString: string = [...new Set(suffixArray)].join(",");
  return uniqueSetString;
};
export const formatStringDepartments = (departments: string[]) => {
  const descriptionsArray: string[] = departments.map((item) => {
    const parts = item.split(".");
    return parts.slice(3).join(".");
  });
  const finalString: string = descriptionsArray.join(",");
  return finalString;
};

/**
 * Groups a flat list of segments by area, pool, and then company.
 * @param segmentByCompany - The flat array of segment data from the API.
 * @returns An object with segments grouped by the nested structure.
 */
export const groupedSegments = (
  segmentByCompany: ICompanyByPool[],
): IGroupedSegments => segmentByCompany.reduce((acc: IGroupedSegments, item) => {
  const {
    area_name, pool_name, pool_id, list_company,
  } = item;
  if (!acc[area_name]) {
    acc[area_name] = {};
  }
  if (!acc[area_name][pool_name]) {
    acc[area_name][pool_name] = {
      pool_id,
      companies: {},
    };
  }

  list_company.forEach((company) => {
    const { company_code, segment_code, segment_name } = company;
    const { companies } = acc[area_name][pool_name];
    if (!companies[company_code]) {
      companies[company_code] = [];
    }
    const segmentExists = companies[company_code].some(
      (s) => s.segment_code === segment_code,
    );
    if (!segmentExists) {
      companies[company_code].push({ segment_code, segment_name });
    }
  });
  return acc;
}, {} as IGroupedSegments);

export const getOptionValue = (
  options: IOptionItemAutoComplete[],
  id: string | number,
) => {
  const foundOption = options.find((r) => String(r.value) === String(id));
  if (!foundOption) return "";
  return foundOption.value;
};

export const getOptionLabel = (
  options: IOptionItemAutoComplete[],
  id: string | number,
  isDefault?: boolean,
) => {
  const foundOption = options.find((r) => String(r.value) === String(id));
  if (!foundOption) return "";
  return isDefault ? String(foundOption.label) : String(foundOption.labelOverridden);
};

export function extractOptionId(value: string) {
  return value.split("•")[0].trim();
}

export const formatPayload = (data: IBusinessTrxFormValues, executor: string = "ADMIN"): ICreateMasterBusinessTrxRequest => {
  const {
    detailTransaksi,
    selectedDepartmentIds,
    resPoolByAreaForValidation,
    resCompanyByPoolForValidation,
  } = data;

  const resPoolByArea: IPoolsByArea[] = resPoolByAreaForValidation
    ? JSON.parse(resPoolByAreaForValidation)
    : [];
  const resCompanyByPool: ICompanyByPool[] = resCompanyByPoolForValidation
    ? JSON.parse(resCompanyByPoolForValidation)
    : [];

  const poolToAreaMap = new Map<string, string>();
  resPoolByArea.forEach((pool) => {
    poolToAreaMap.set(pool.pool_id, pool.area_id);
  });

  const companyToRelationMap = new Map<string, string>();
  resCompanyByPool.forEach((pool) => {
    pool.list_company.forEach((company) => {
      const key = `${pool.pool_id}.${company.company_code}`;
      companyToRelationMap.set(key, company.company_relation);
    });
  });

  const assignments = selectedDepartmentIds.map((departmentId) => {
    const parts = departmentId.split(".");
    const pool_id = parts[0];
    const company_code = parts[1];
    const code_segment = parts[2];
    const department = parts.slice(3).join(".");
    const area_id = poolToAreaMap.get(pool_id) || "";
    const company_relation = companyToRelationMap.get(`${pool_id}.${company_code}`) || "";

    return {
      company_code,
      area_id,
      pool_id,
      code_segment,
      department,
      code_type: CodeType.Pool,
      company_relation: company_relation || "",
      is_active: true,
    };
  });

  return {
    trx_code: detailTransaksi.trx_code,
    trx_desc: detailTransaksi.deskripsi,
    sap_bktxt: detailTransaksi.deskripsiSap,
    gnlldr: detailTransaksi.gl,
    trx_type: detailTransaksi.tipeTransaksi,
    assignment_pools: assignments,
    create_by: executor,
  };
};

export const formatPayloadEdit = (
  id: string,
  data: IBusinessTrxFormValues,
  executor: string = "ADMIN",
): IEditMasterBusinessTrxRequest => ({
  trx_code: data.detailTransaksi.trx_code,
  trx_desc: data.detailTransaksi.deskripsi,
  sap_bktxt: data.detailTransaksi.deskripsiSap,
  gnlldr: data.detailTransaksi.gl,
  trx_type: data.detailTransaksi.tipeTransaksi,
  code_type: data.detailTransaksi.code_type,
  company_code: formatStringCompanies(data.selectedCompanyIds),
  area_id: formatStringIds(data.selectedAreaIds),
  pool_id: formatStringIds(data.selectedPoolIds),
  code_segment: formatStringSegments(data.selectedSegmentIds),
  department: formatStringDepartments(data.selectedDepartmentIds),
  company_relation: formatStringCompanyRelations(data.selectedCompanyRelations),
  is_active: true,
  updated_by: executor,
  id,
});

export const stepsConfig: StepConfigType[] = [
  { label: "Detail transaksi", name: "stepDetailTrx", Icon: Circle1 },
  { label: "Area", name: "stepArea", Icon: Circle2 },
  { label: "Pool", name: "stepPool", Icon: Circle3 },
  { label: "Perusahaan", name: "stepPerusahaan", Icon: Circle4 },
  { label: "Segmen", name: "stepSegmen", Icon: Circle5 },
  { label: "Departemen", name: "stepDepartemen", Icon: Circle6 },
];
